import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Spinner } from 'react-bootstrap';
import TableComponent from '../../../../components/table/TableComponent';
import RecipeModel from '../../../../components/model/RecipeModel';
import { useAlert } from 'react-alert';
import { CreateButton } from '../../../../components/buttons/CreateButton';
import { useOnMount } from '../../../../components/common/Hooks';
import { setDirection, setPage, setSortField } from '../../../../redux/slices/PaginationSlice';
import { deleteRecipeRequest, fetchRecipePdf, getRecipes } from './RecipeService';
import { SearchFilter } from '../../../../components/filter/SearchFilter';
import { ConfirmationModal } from '../../../../components/modal/ConfirmationModal';
import { isMobile } from 'react-device-detect';
import { ToggleFilterButton } from '../../../../components/buttons/ToggleFilterButton';
import { EditButton } from '../../../../components/buttons/EditButton';
import { DeleteButton } from '../../../../components/buttons/DeleteButton';
import { CopyButton } from '../../../../components/buttons/CopyButton';
import { FileExtension, saveFile } from '../../../../components/common/FileUtils';

const RecipePage = () => {

    const onMount = useOnMount();
    const { t, i18n } = useTranslation();
    const alert = useAlert();
    const [loading, setLoading] = useState(true);
    const dispatch = useDispatch();
    const pagination = useSelector((state) => state.pagination);
    const [recipes, setRecipes] = useState([]);
    const navigate = useNavigate();
    const [filterValue, setFilterValue] = useState('');
    const [isConfirmOpen, setIsConfirmOpen] = useState(false);
    const [itemToDelete, setItemToDelete] = useState(null);
    const handleCloseModal = () => setIsConfirmOpen(false);

    useEffect(() => {
        if (onMount) {
            init();
        } else {
            load();
        }
    }, [pagination.page, pagination.sortByField, pagination.sortingDirection]);

    const init = () => {
        if (pagination.sortByField === 'title'
            && pagination.page === 0
            && pagination.sortingDirection === 'ASC') {
            load();
        } else {
            dispatch(setSortField('title'));
            dispatch(setPage(0));
            dispatch(setDirection('ASC'));
        }
    };

    const load = () => {
        let request = {
            page: pagination.page,
            size: pagination.size,
            ascDesc: pagination.sortingDirection,
            sortField: pagination.sortByField,
            lang: i18n.language.toUpperCase(),
            title: filterValue,
        };
        getRecipes(request)
            .then((response) => setRecipes(response.data))
            .catch(() => alert.error(t('alert:loadingError')))
            .finally(() => setLoading(false));
    };

    const openConfirmation = (recipe) => {
        setItemToDelete(recipe);
        setIsConfirmOpen(true);
    };

    const deleteRecipe = () => {
        deleteRecipeRequest(itemToDelete.id)
            .then(() => {
                load();
                alert.success(t('alert:deleteSuccess'));
            })
            .catch(() => alert.error(t('alert:deleteError')));
        handleCloseModal();
    };

    const editRecipe = (recipeObj) => {
        navigate('/recipeForm', { state: { id: recipeObj.id, copy: false } });
    };

    const copyRecipe = (recipeObj) => {
        navigate('/recipeForm', { state: { id: recipeObj.id, copy: true } });
    };

    const createNewRecipe = () => {
        navigate('/recipeForm');
    };

    const downloadPdf = (recipeObj) => {
        fetchRecipePdf(recipeObj.id)
            .then((response) => saveFile(response.data, recipeObj.title, FileExtension.PDF))
            .catch(() => alert.error(t('alert:loadingError')));
    };

    const getButtons = () => {
        return [
            { element: <EditButton onClick={editRecipe} /> },
            { element: <DeleteButton onClick={openConfirmation} /> },
            { element: <CopyButton onClick={copyRecipe} /> },
            { element: <button className={'button--main success-button'} onClick={downloadPdf}>PDF</button> },
        ];
    };

    return (
        <>
            <div className={isMobile ? 'mobile-filter-container' : 'filter-container'}>
                <div>
                    <CreateButton onClick={createNewRecipe} />
                    {isMobile ? <ToggleFilterButton></ToggleFilterButton> : <></>}
                </div>
                <SearchFilter doFilter={load} value={filterValue} setValue={setFilterValue}></SearchFilter>
            </div>
            <ConfirmationModal
                message={t('recipe:deleteConfirm') + (itemToDelete != null ? itemToDelete.title : '') + '?'}
                isShowing={isConfirmOpen}
                onConfirm={deleteRecipe}
                onClose={handleCloseModal}>
            </ConfirmationModal>
            {loading ? (
                <Spinner animation='border' className='container' />
            ) : (
                <TableComponent
                    model={RecipeModel}
                    translationsGroup='recipe:'
                    columnsNames={[
                        'title',
                        'allCarbohydrates',
                        'allProteins',
                        'allFat',
                        'allEnergyValueKcal',
                    ]}
                    totalElements={recipes.totalElements}
                    contents={recipes.content}
                    buttons={getButtons()}
                />
            )}
        </>
    );
};
export default RecipePage;
